/**
 * OrgEmployee Store Actions
 *
 * Contains all action implementations for the orgEmployee store.
 *
 * @module @store/orgEmployee/orgEmployeeActions
 */

import { apiService } from '@shared/services/apiService';
import type { OrgEmployee } from '@shared/types';
import type { OrgEmployeeState, OrgEmployeeActions } from './orgEmployeeTypes';

/**
 * Initial state for reset operations.
 */
function getInitialState(): OrgEmployeeState {
    return {
        orgEmployee: null,
        isLoading: false,
        error: null,
    };
}

/**
 * Creates the orgEmployee actions object.
 *
 * @param set - Zustand set function
 * @param get - Zustand get function
 * @returns OrgEmployeeActions object
 */
export function createOrgEmployeeActions(
    set: (
        partial:
            | Partial<OrgEmployeeState>
            | ((state: OrgEmployeeState) => Partial<OrgEmployeeState>),
    ) => void,
    get: () => OrgEmployeeState,
): OrgEmployeeActions {
    return {
        /**
         * Fetches the org-employee data from the API.
         * @param accessToken - Auth0 access token
         */
        fetchOrgEmployee: async (accessToken: string): Promise<void> => {
            // Prevent duplicate calls if already fetching by checking isLoading state
            // This handles StrictMode double-mounting in development
            if (get().isLoading) {
                return;
            }

            set({ isLoading: true, error: null });
            try {
                const orgEmployee: OrgEmployee =
                    await apiService.orgEmployee.get(accessToken);
                set({ orgEmployee, isLoading: false });
            } catch (err) {
                set({
                    error:
                        err instanceof Error
                            ? err.message
                            : 'Failed to load employee data',
                    isLoading: false,
                });
            }
        },

        /**
         * Resets the store to initial state.
         */
        reset: (): void => set(getInitialState()),
    };
}
